import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { firstValueFrom } from 'rxjs';
import { CartolaService } from '../../services/cartola.service';
import { WalletVesClientService } from '../../services/wallet-ves-client.service';
import { CuentasBancariasVesService } from '../../services/cuentas-bancarias-ves.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { AppState } from 'src/app/app.reducer';
import Swal from 'sweetalert2';
import { puedeRecargarVes } from 'src/app/shared/utils/saldo-ves';

@Component({
  selector: 'app-charge-account',
  templateUrl: './charge-account.component.html',
  styleUrls: ['./charge-account.component.sass']
})
export class ChargeAccountComponent implements OnInit, OnDestroy {
  data: any[] = [];
  dataVes: any[] = [];
  tipoId = '';
  user: any;
  referenciaVes = '';
  montoVes = '';
  loadingReferencia = false;
  referenciaSent = false;
  cuentaVesSeleccionada: any = null;
  /**
   * Recarga de bolívares (cuentas VES + referencia): solo el VE
   * (`puedeRecargarVes`); el resto ve la recarga CLP.
   * 30/10/2026: el chileno no recarga bolívares. Ya no se lee `?moneda=VES`,
   * que le abría esta recarga: con o sin ese parámetro ve la suya, la CLP.
   */
  modoVes = false;
  /** Modo con el que se cargaron las cuentas: evita recargar si no cambió. */
  private modoCargado: 'VES' | 'CLP' | null = null;

  private authSubscription?: Subscription;

  constructor(
    private cartolaService: CartolaService,
    private walletVesClientService: WalletVesClientService,
    private cuentasBancariasVesService: CuentasBancariasVesService,
    private clipboard: Clipboard,
    private snackBar: MatSnackBar,
    private spinner: SpinnerService,
    private router: Router,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.authSubscription = this.store.select('auth').subscribe(({ user }: any) => {
      this.user = user;
      this.tipoId = user?.tipoId || '';
      this.modoVes = puedeRecargarVes(this.tipoId);

      const modo = this.modoVes ? 'VES' : 'CLP';
      if (modo === this.modoCargado) return;
      this.modoCargado = modo;
      // Al cambiar de modo (llega otro usuario) no queda una referencia a medias.
      this.referenciaVes = '';
      this.montoVes = '';
      this.referenciaSent = false;
      this.cuentaVesSeleccionada = null;
      if (this.modoVes) {
        this.loadDataVes();
      } else {
        this.loadData();
      }
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  async loadData(): Promise<void> {
    const spinnerRef = this.spinner.start();
    const accounts = await this.cartolaService.getEmisores();
    this.data = accounts;
    this.spinner.stop(spinnerRef);
  }

  loadDataVes(): void {
    const spinnerRef = this.spinner.start();
    this.cuentasBancariasVesService.getActiveClient().subscribe({
      next: (cuentas) => {
        this.dataVes = cuentas.map((c) => ({
          banco: c.banco,
          tipoCuenta: c.tipoCuenta,
          titular: c.nombreTitular,
          numeroCuenta: c.numeroCuenta,
          cedula: c.rifCedula,
        }));
        if (this.dataVes.length === 1) {
          this.cuentaVesSeleccionada = this.dataVes[0];
        }
        this.spinner.stop(spinnerRef);
      },
      error: () => {
        this.snackBar.open('Error al cargar cuentas VES', 'Ok', { duration: 3000 });
        this.spinner.stop(spinnerRef);
      },
    });
  }

  copyData(item: any): void {
    const textToCopy = `${item.banco}\nCuenta Corriente\n${item.cuenta}\n${item.numeroCuenta}\nRut: ${this.formatRut(item.rutemisor)}`;

    this.clipboard.copy(textToCopy);
    this.snackBar.open('Datos copiados exitosamente', '', {
      duration: 2500,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['aguacate-snackbar'],
    });
  }

  copyDataVes(item: any): void {
    const textToCopy = `${item.banco}\nCuenta ${item.tipoCuenta}\n${item.titular}\n${item.numeroCuenta}\nRIF: ${item.cedula}`;

    this.clipboard.copy(textToCopy);
    this.snackBar.open('Datos copiados exitosamente', '', {
      duration: 2500,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['aguacate-snackbar'],
    });
  }

  formatRut(rut: string): string {
    if (!rut) return '';

    const rutDigits = rut.replace(/[^\d]/g, '');
    const rutLength = rutDigits.length;

    if (rutLength < 3) return rut;

    const lastDigit = rutDigits.charAt(rutLength - 1);
    const mainPart = rutDigits.substring(0, rutLength - 1);

    let formattedRut = mainPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

    formattedRut = formattedRut + '-' + lastDigit;

    return formattedRut;
  }

  async enviarReferencia(): Promise<void> {
    // 30/10/2026: fuera del modo VES no se informa nada, aunque se fuerce el
    // formulario. Nest todavía acepta la referencia de un chileno.
    if (!this.modoVes) return;
    if (!this.referenciaValida || this.loadingReferencia || this.referenciaSent) return;

    this.loadingReferencia = true;

    const cuenta = this.cuentaVesSeleccionada || this.dataVes[0];
    const payload = {
      userId: this.user.usuarioId,
      banco: cuenta.banco,
      cuenta: cuenta.numeroCuenta,
      tipoCuenta: cuenta.tipoCuenta,
      titular: cuenta.titular,
      referencia: this.referenciaVes.trim(),
      monto: parseFloat(this.montoVes.replace(',', '.')),
    };

    try {
      await firstValueFrom(this.walletVesClientService.crearReferenciaVes(payload));

      this.referenciaSent = true;

      await Swal.fire({
        icon: 'success',
        title: 'Referencia enviada',
        text: 'Tu referencia ha sido registrada. Tu saldo se actualizará una vez verificada la transferencia.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1e5a3f',
      });

      this.router.navigate(['/wallet/home']);
    } catch (error: any) {
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error?.message || 'No se pudo enviar la referencia. Intenta de nuevo.',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#1e5a3f',
      });
    } finally {
      this.loadingReferencia = false;
    }
  }

  get referenciaValida(): boolean {
    const tieneReferencia = this.referenciaVes.trim().length > 0 && this.montoVes.trim().length > 0;
    const tieneCuenta = this.dataVes.length === 1 || this.cuentaVesSeleccionada !== null;
    return tieneReferencia && tieneCuenta;
  }

  goToHome(): void {
    this.router.navigate(['/wallet/home']);
  }
}
